import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import firebaseService from '../services/firebaseService';

function QRCodeHistory() {
  const [qrHistory, setQrHistory] = useState([]);
  const [expandedCode, setExpandedCode] = useState(null);
  const [loading, setLoading] = useState(true);
  const { currentUser: user } = useAuth();

  useEffect(() => {
    loadQRHistory();
  }, []);

  const loadQRHistory = async () => {
    try {
      setLoading(true);
      const qrCodes = await firebaseService.getQRCodes();
      setQrHistory(qrCodes);
    } catch (error) {
      console.error('Error loading QR history:', error);
    } finally {
      setLoading(false);
    }
  };

  const saveToHistory = async (qrCode) => {
    try {
      console.log('🔄 Saving QR code to Firestore:', qrCode.referralCode);
      
      // Check if this referral code already exists
      const existingQR = await firebaseService.getQRCodeByReferralCode(qrCode.referralCode);
      console.log('📋 Existing QR found:', existingQR ? 'Yes' : 'No');
      
      if (existingQR) {
        // Update existing QR code
        console.log('🔄 Updating existing QR code...');
        await firebaseService.updateQRCode(existingQR.id, {
          generationCount: existingQR.generationCount + 1,
          qrDataUrl: qrCode.qrDataUrl, // Update with latest QR image
          url: qrCode.url // Update with latest URL
        });
        console.log('✅ QR code updated successfully');
      } else {
        // Create new QR code
        console.log('➕ Creating new QR code...');
        const result = await firebaseService.saveQRCode({
          referralCode: qrCode.referralCode,
          url: qrCode.url,
          qrDataUrl: qrCode.qrDataUrl,
          createdBy: user.id,
          createdByName: user.username
        });
        console.log('✅ QR code created successfully:', result);
      }
      
      // Reload history
      console.log('🔄 Reloading QR history...');
      await loadQRHistory();
      console.log('✅ QR history reloaded');
    } catch (error) {
      console.error('❌ Error saving QR to history:', error);
      alert('Failed to save QR code: ' + error.message);
    }
  };

  const deleteCode = async (id) => {
    if (window.confirm('Are you sure you want to delete this QR code?')) {
      try {
        await firebaseService.deleteQRCode(id);
        await loadQRHistory();
      } catch (error) {
        console.error('Error deleting QR code:', error);
      }
    }
  };

  const clearHistory = async () => {
    if (window.confirm('Are you sure you want to delete all QR codes? This cannot be undone.')) {
      try {
        // Delete all QR codes
        for (const qrCode of qrHistory) {
          await firebaseService.deleteQRCode(qrCode.id);
        }
        await loadQRHistory();
      } catch (error) {
        console.error('Error clearing QR history:', error);
      }
    }
  };

  const copyUrl = (url) => {
    navigator.clipboard.writeText(url);
    alert('URL copied to clipboard!');
  };

  const downloadQR = (qrDataUrl, referralCode) => {
    const link = document.createElement('a');
    link.download = `crappermapper-qr-${referralCode}.png`;
    link.href = qrDataUrl;
    link.click();
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString();
  };

  // Make this function available globally for QRCodeGenerator to call
  if (typeof window !== 'undefined') {
    window.saveQRToHistory = saveToHistory;
  }

  if (loading) {
    return (
      <div className="qr-history-container">
        <div className="qr-history-loading">
          <div className="loading-spinner"></div>
          <p>Loading QR codes...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="qr-history-container">
      <div className="qr-history-header">
        <h3>📋 QR Code History</h3>
        {qrHistory.length > 0 && (
          <button 
            className="clear-history-btn"
            onClick={clearHistory}
          >
            🗑️ Clear All
          </button>
        )}
      </div>

      {qrHistory.length === 0 ? (
        <div className="qr-history-empty">
          <p>No QR codes generated yet.</p>
          <p>Generate your first QR code above to see it here!</p>
        </div>
      ) : (
        <div className="qr-history-list">
          {qrHistory.map((item) => (
            <div key={item.id} className="qr-history-item">
              <div className="qr-history-info">
                <div className="qr-code-header">
                  <h4>Referral Code: <strong>{item.referralCode}</strong></h4>
                  <div className="qr-history-actions">
                    <button 
                      onClick={() => copyUrl(item.url)}
                      className="copy-btn"
                      title="Copy URL"
                    >
                      📋
                    </button>
                    <button 
                      onClick={() => downloadQR(item.qrDataUrl, item.referralCode)}
                      className="download-btn"
                      title="Download QR Code"
                    >
                      💾
                    </button>
                    <button 
                      onClick={() => setExpandedCode(expandedCode === item.id ? null : item.id)}
                      className="expand-btn"
                      title="View QR Code"
                    >
                      {expandedCode === item.id ? '👁️‍🗨️' : '👁️'}
                    </button>
                    <button 
                      onClick={() => deleteCode(item.id)}
                      className="delete-btn"
                      title="Delete"
                    >
                      🗑️
                    </button>
                  </div>
                </div>
                
                <div className="qr-history-meta">
                  <span>Created: {formatDate(item.createdAt)}</span>
                  {item.lastGenerated && item.lastGenerated !== item.createdAt && (
                    <span>Last used: {formatDate(item.lastGenerated)}</span>
                  )}
                  <span>Generated: {item.generationCount || 1} time{(item.generationCount || 1) !== 1 ? 's' : ''}</span>
                  {item.createdByName && (
                    <span>Created by: {item.createdByName}</span>
                  )}
                </div>

                {expandedCode === item.id && (
                  <div className="qr-code-preview">
                    <img 
                      src={item.qrDataUrl} 
                      alt={`QR Code for ${item.referralCode}`}
                    />
                    <div className="qr-url-display">
                      <strong>URL:</strong>
                      <code>{item.url}</code>
                    </div>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      <style jsx>{`
        .qr-history-container {
          background: white;
          border-radius: 12px;
          padding: 30px;
          border: 1px solid #e9ecef;
          margin-top: 30px;
        }

        .qr-history-loading {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 40px;
          color: #6c757d;
        }

        .loading-spinner {
          width: 40px;
          height: 40px;
          border: 4px solid rgba(87, 82, 232, 0.3);
          border-top: 4px solid #5752e8;
          border-radius: 50%;
          animation: spin 1s linear infinite;
          margin-bottom: 20px;
        }

        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }

        .qr-history-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 25px;
        }

        .qr-history-header h3 {
          color: #495057;
          margin: 0;
        }

        .clear-history-btn {
          background: #dc3545;
          color: white;
          border: none;
          padding: 8px 16px;
          border-radius: 6px;
          cursor: pointer;
          font-size: 0.9rem;
        }

        .clear-history-btn:hover {
          background: #c82333;
        }

        .qr-history-empty {
          text-align: center;
          padding: 40px;
          color: #6c757d;
        }

        .qr-history-empty p {
          margin-bottom: 10px;
        }

        .qr-history-list {
          display: flex;
          flex-direction: column;
          gap: 15px;
        }

        .qr-history-item {
          border: 1px solid #dee2e6;
          border-radius: 8px;
          padding: 20px;
          background: #f8f9fa;
          transition: all 0.3s ease;
        }

        .qr-history-item:hover {
          border-color: #5752e8;
          box-shadow: 0 2px 8px rgba(87, 82, 232, 0.1);
        }

        .qr-code-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 10px;
        }

        .qr-code-header h4 {
          margin: 0;
          color: #495057;
        }

        .qr-history-actions {
          display: flex;
          gap: 8px;
        }

        .qr-history-actions button {
          background: none;
          border: 1px solid #dee2e6;
          border-radius: 4px;
          padding: 6px 8px;
          cursor: pointer;
          font-size: 0.9rem;
          transition: all 0.2s ease;
        }

        .qr-history-actions button:hover {
          background: #e9ecef;
        }

        .copy-btn:hover {
          background: #d4edda;
          border-color: #2d6657;
        }

        .download-btn:hover {
          background: #d1ecf1;
          border-color: #17a2b8;
        }

        .expand-btn:hover {
          background: #fff3cd;
          border-color: #ffc107;
        }

        .delete-btn:hover {
          background: #f8d7da;
          border-color: #dc3545;
        }

        .qr-history-meta {
          display: flex;
          gap: 20px;
          font-size: 0.85rem;
          color: #6c757d;
          margin-bottom: 15px;
          flex-wrap: wrap;
        }

        .qr-code-preview {
          margin-top: 15px;
          padding-top: 15px;
          border-top: 1px solid #dee2e6;
        }

        .qr-code-preview img {
          max-width: 200px;
          height: auto;
          border: 2px solid #dee2e6;
          border-radius: 8px;
          padding: 10px;
          background: white;
          margin-bottom: 15px;
        }

        .qr-url-display {
          background: white;
          padding: 10px;
          border-radius: 4px;
          border: 1px solid #dee2e6;
        }

        .qr-url-display code {
          color: #c7254e;
          font-family: monospace;
          font-size: 0.9em;
          word-break: break-all;
        }

        @media (max-width: 768px) {
          .qr-history-header {
            flex-direction: column;
            gap: 15px;
            align-items: stretch;
          }

          .qr-code-header {
            flex-direction: column;
            gap: 10px;
            align-items: stretch;
          }

          .qr-history-meta {
            flex-direction: column;
            gap: 5px;
          }

          .qr-history-actions {
            justify-content: center;
          }
        }
      `}</style>
    </div>
  );
}

export default QRCodeHistory;
